/*
 * Helpers for the AI features: turning the AI's plain text into editor HTML
 * (and back), reading the "Catch me up" summary and the server's error text.
 */

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const BULLET = /^\s*(?:[-*•–]|\d+[.)])\s+/;

const isBullet = (line: string) => BULLET.test(line);

const stripBullet = (line: string) => line.replace(BULLET, "").trim();

/** Removes markdown bold/italic markers the AI sometimes adds. */
const stripMarkdown = (line: string) =>
  line
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .trim();

/**
 * AI plain text -> Quill HTML. Paragraphs (blank line between) become <p>
 * with an empty line between them, "- " lines become a bullet list. HTML is
 * escaped; {{placeholders}} are kept as they are.
 */
export const plainTextToQuillHtml = (text: string): string => {
  const blocks = (text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((block) => block.split("\n").filter((line) => line.trim()))
    .filter((lines) => lines.length);

  return blocks
    .map((lines) => {
      let html = "";
      let bullets: string[] = [];
      const flush = () => {
        if (bullets.length) {
          html += `<ul>${bullets
            .map((item) => `<li>${item}</li>`)
            .join("")}</ul>`;
          bullets = [];
        }
      };
      lines.forEach((line) => {
        if (isBullet(line)) {
          bullets.push(escapeHtml(stripMarkdown(stripBullet(line))));
        } else {
          flush();
          html += `<p>${escapeHtml(stripMarkdown(line))}</p>`;
        }
      });
      flush();
      return html;
    })
    .join("<p><br></p>");
};

const decodeEntities = (value: string) =>
  value
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, String.fromCharCode(34))
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, "&");

/**
 * Quill HTML -> plain text (one line per paragraph, "- " for list items).
 * Used to send the current text to the AI and to see if there is any text.
 */
export const quillHtmlToPlainText = (html: string): string =>
  decodeEntities(
    (html || "")
      .replace(/<li[^>]*>/gi, "- ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, "\n")
      .replace(/<[^>]+>/g, ""),
  )
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const hasText = (html: string) => quillHtmlToPlainText(html) !== "";

export type SummarySection = { title: string; bullets: string[] };
export type ParsedSummary = { lead: string; sections: SummarySection[] };

const LEAD_LABEL = /^where (it|things|this) stands?\b[\s:.\-–—]*/i;
const KNOWN_HEADINGS = [
  /^done( so far)?$/i,
  /^completed$/i,
  /^still to do$/i,
  /^to do$/i,
  /^next steps?$/i,
  /^waiting on(\s*\/\s*blockers)?$/i,
  /^blockers$/i,
  /^latest client contact$/i,
  /^client contact$/i,
];

/** "## Done so far:", "**Still to do**" -> "Done so far" (or null if not a heading). */
const headingTitle = (line: string): string | null => {
  const markdown = /^\s*#{1,6}\s+/.test(line);
  const cleaned = stripMarkdown(line.replace(/^\s*#{1,6}\s+/, ""))
    .replace(/[:：]\s*$/, "")
    .trim();
  if (!cleaned || isBullet(line)) return null;
  if (markdown) return cleaned;
  const endsWithColon = /[:：]\s*\**\s*$/.test(line);
  const wholeBold = /^\s*\*\*[^*]+\*\*:?\s*$/.test(line);
  if (cleaned.length > 60) return null;
  if (KNOWN_HEADINGS.some((pattern) => pattern.test(cleaned))) return cleaned;
  if ((endsWithColon || wholeBold) && cleaned.split(/\s+/).length <= 6) {
    return cleaned;
  }
  return null;
};

/**
 * Reads the "Catch me up" text: a lead ("Where it stands…") and sections
 * (heading line + "- " bullets). Copes with markdown headings, bold, colons,
 * "*"/"•"/numbered bullets and text lines without a bullet.
 */
export const parseSummary = (text: string): ParsedSummary => {
  const lines = (text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd());
  const lead: string[] = [];
  const sections: SummarySection[] = [];
  let current: SummarySection | null = null;
  let inLead = true;

  for (const raw of lines) {
    if (!raw.trim()) continue;
    const title = headingTitle(raw);
    if (title !== null) {
      if (LEAD_LABEL.test(title) && !sections.length) {
        // "Where it stands" used as a heading: what follows is the lead.
        const rest = title.replace(LEAD_LABEL, "").trim();
        if (rest) lead.push(rest);
        inLead = true;
        current = null;
        continue;
      }
      current = { title, bullets: [] };
      sections.push(current);
      inLead = false;
      continue;
    }
    const content = stripMarkdown(isBullet(raw) ? stripBullet(raw) : raw);
    if (!content) continue;
    if (inLead && !current) {
      if (isBullet(raw) && lead.length) {
        // Bullets right after the lead with no heading.
        current = { title: "", bullets: [content] };
        sections.push(current);
        inLead = false;
        continue;
      }
      lead.push(content.replace(LEAD_LABEL, "").trim() || content);
      continue;
    }
    if (current) current.bullets.push(content);
  }

  return { lead: lead.filter(Boolean).join(" "), sections };
};

/** "just now", "5 minutes ago", "2 hours ago", "yesterday", "3 days ago". */
export const timeAgo = (iso: string, now: Date = new Date()): string => {
  const time = new Date(iso).getTime();
  if (!iso || Number.isNaN(time)) return "";
  const minutes = Math.floor((now.getTime() - time) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

const DEFAULT_ERROR = "AI couldn't answer right now. Please try again.";

const cleanMessage = (value: string) => {
  const text = value
    .trim()
    .replace(/^"([\s\S]*)"$/, "$1")
    .trim();
  if (!text || /^<!doctype|^<html/i.test(text)) return "";
  return text.length > 300 ? `${text.slice(0, 297)}…` : text;
};

/**
 * The server answers 400/403 with a plain-English message (plain text, a JSON
 * string or an object with message/detail/title). Shows that, or a fallback.
 */
export const aiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== "object") return DEFAULT_ERROR;
  const { status, data, originalStatus } = error as {
    status?: number | string;
    data?: unknown;
    originalStatus?: number;
  };
  if (typeof data === "string") {
    const message = cleanMessage(data);
    if (message) return message;
  } else if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    const message = ["message", "detail", "error", "title"]
      .map((key) => record[key])
      .find((value) => typeof value === "string" && value.trim());
    if (typeof message === "string") return cleanMessage(message);
  }
  const code = typeof status === "number" ? status : originalStatus;
  if (status === "FETCH_ERROR") {
    return "Couldn't reach ProCharted. Check your connection and try again.";
  }
  if (code === 429)
    return "You've reached today's AI limit. Try again tomorrow.";
  if (code === 403) return "You don't have access to AI features.";
  return DEFAULT_ERROR;
};
